import moment from 'moment';
import { ENERGISYNC_KEY } from './constants';

const pad2 = (value) => String(value).padStart(2, '0');

const isNum = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * The device stores 0 for "no date", which is not a real day/month/year. Times
 * are the opposite — 0 is midnight — so they get their own range checks below.
 */
const isRealDatePart = (value) => isNum(value) && value > 0;

const isHour = (value) => isNum(value) && value >= 0 && value <= 23;
const isMinute = (value) => isNum(value) && value >= 0 && value <= 59;

/** react-select options for the property dropdown, de-duplicated by house id. */
export const buildHouseOptions = (housesList) => {
  const seen = new Set();
  const options = [];

  for (const house of Array.isArray(housesList) ? housesList : []) {
    const value = house?.houseAccessCode;
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({ value, label: house?.houseName || value });
  }

  return options;
};

/** Every EnergiSync device on a house, flattened across rooms. */
export const collectEnergiSyncDevices = (house) => {
  const rooms = Array.isArray(house?.rooms) ? house.rooms : [];

  return rooms
    .flatMap((room) =>
      (room?.[ENERGISYNC_KEY] || []).map((device) => {
        const name = typeof device?.name === 'string' ? device.name.trim() : '';
        return {
          deviceId: device?.deviceId,
          name: name || device?.deviceId,
          hasName: name !== '',
          roomName: room?.roomName || 'Unassigned',
          roomId: room?._id,
          raw: device,
        };
      })
    )
    .filter((device) => device.deviceId);
};

const readObject = (device, key) => {
  const value = (device?.raw ?? device)?.[key];
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
};

/** What the device reports it is running, or `{}` when it carries nothing. */
export const readSection = (device, section) => readObject(device, section.readKey);

/** What the cloud has last asked the device for, or `{}` when nothing is queued. */
export const readExpected = (device, section) => readObject(device, section.writeKey);

/** Every raw device key a section's controls map onto, timestamp excluded. */
export const rawKeysOf = (section) =>
  section.fields.flatMap((field) => {
    if (field.type === 'time') return [field.hourKey, field.minuteKey];
    if (field.type === 'date') return [field.dayKey, field.monthKey, field.yearKey];
    if (field.type === 'timestamp') return [];
    return [field.rawKey];
  });

// The service has been seen storing `active` as an empty string, so booleans are
// compared as booleans and everything else as numbers rather than by identity.
const sameRawValue = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') return !!a === !!b;
  return Number(a || 0) === Number(b || 0);
};

/**
 * Whether a change has been asked for that the device has not applied yet —
 * `expected*` present and differing from `current*` on any key the page writes.
 */
export const isPendingSync = (section, current, expected) => {
  if (!expected || Object.keys(expected).length === 0) return false;
  return rawKeysOf(section).some((key) => !sameRawValue(expected[key], current?.[key]));
};

export const matchesSearch = (device, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    device.deviceId?.toLowerCase().includes(q) ||
    device.name?.toLowerCase().includes(q) ||
    device.roomName?.toLowerCase().includes(q)
  );
};

export const isBlank = (value) => value === undefined || value === null || value === '';

/**
 * Raw device numbers folded into the one value per control the editor and the
 * table both work off — `on_hour: 19, on_minute: 0` becomes `on: '19:00'`.
 */
export const toFormValues = (section, raw) => {
  const form = {};

  for (const field of section.fields) {
    switch (field.type) {
      case 'boolean':
        form[field.key] = !!raw[field.rawKey];
        break;

      case 'time': {
        const hour = raw[field.hourKey];
        const minute = raw[field.minuteKey];
        form[field.key] = isHour(hour) && isMinute(minute) ? `${pad2(hour)}:${pad2(minute)}` : '';
        break;
      }

      case 'date': {
        const day = raw[field.dayKey];
        const month = raw[field.monthKey];
        const year = raw[field.yearKey];
        form[field.key] =
          isRealDatePart(day) && isRealDatePart(month) && isRealDatePart(year)
            ? `${year}-${pad2(month)}-${pad2(day)}`
            : '';
        break;
      }

      case 'timestamp':
        form[field.key] = isNum(raw[field.rawKey]) ? raw[field.rawKey] : null;
        break;

      default:
        form[field.key] = isBlank(raw[field.rawKey]) ? '' : String(raw[field.rawKey]);
    }
  }

  return form;
};

/**
 * The inverse — editor values split back into the flat numbers the service
 * takes. A cleared control writes 0 rather than dropping the key, so the
 * schedule object keeps the same shape it arrived in.
 */
export const toRawValues = (section, form) => {
  const raw = {};

  for (const field of section.fields) {
    switch (field.type) {
      case 'boolean':
        raw[field.rawKey] = !!form[field.key];
        break;

      case 'time': {
        const [hour, minute] = String(form[field.key] || '').split(':');
        raw[field.hourKey] = Number(hour) || 0;
        raw[field.minuteKey] = Number(minute) || 0;
        break;
      }

      case 'date': {
        const [year, month, day] = String(form[field.key] || '').split('-');
        raw[field.yearKey] = Number(year) || 0;
        raw[field.monthKey] = Number(month) || 0;
        raw[field.dayKey] = Number(day) || 0;
        break;
      }

      case 'timestamp':
        // Device-owned and absent from the update payload.
        break;

      default:
        raw[field.rawKey] = isBlank(form[field.key]) ? null : form[field.key];
    }
  }

  return raw;
};

/**
 * The selectable window for a date field, as the `min` / `max` a native date
 * input takes. Absent offsets mean that end is unbounded.
 */
export const dateBounds = (field) => {
  const bounds = {};
  if (isNum(field.minOffsetDays)) bounds.min = moment().add(field.minOffsetDays, 'days').format('YYYY-MM-DD');
  if (isNum(field.maxOffsetDays)) bounds.max = moment().add(field.maxOffsetDays, 'days').format('YYYY-MM-DD');
  return bounds;
};

/**
 * The `min` a date input should actually use. An inclusive `after` rule floors
 * this field with a sibling's value — the end of a window cannot precede its
 * start — and the later of the two bounds wins.
 */
export const effectiveDateMin = (field, form = {}) => {
  const { min } = dateBounds(field);
  const floor = field.after?.inclusive ? form[field.after.field] : null;
  if (min && floor) return min > floor ? min : floor;
  return min || floor || undefined;
};

/** Editor value rendered as the short string that goes on a summary chip. */
export const formatFieldValue = (field, value) => {
  if (field.type === 'boolean') return value ? 'On' : 'Off';
  if (isBlank(value)) return '—';

  switch (field.type) {
    case 'time': {
      const [hour, minute] = value.split(':').map(Number);
      const suffix = hour >= 12 ? 'PM' : 'AM';
      const display = hour % 12 === 0 ? 12 : hour % 12;
      return `${display}:${pad2(minute)} ${suffix}`;
    }
    case 'date':
      return moment(value, 'YYYY-MM-DD').format('D MMM YYYY');
    case 'timestamp':
      return moment(value).format('D MMM YYYY, h:mm A');
    default:
      return String(value);
  }
};

/**
 * Whether the section is running, configured but switched off, or absent.
 *
 * Zero is a real value on the device — hour 0 is 12 AM — so an all-zeros
 * schedule is configuration that happens to sit at midnight, not an empty one.
 * Only a device that carries no schedule object at all reads as "not set", and
 * that is already visible in the editor values: a missing schedule leaves the
 * time fields blank where a zeroed one leaves them at '00:00'.
 */
export const sectionState = (section, form) => {
  if (form.active) return 'on';

  const configured = section.fields.some(
    (field) => field.key !== 'active' && field.type !== 'timestamp' && !isBlank(form[field.key])
  );

  return configured ? 'off' : 'unset';
};

/**
 * Blocking problems with the editor values, keyed by field.
 *
 * Every editable control is required — a half-filled schedule is not something
 * the device can act on. The relational checks below stay gated behind `active`
 * so a schedule that came back holding contradictory values can always be
 * switched off without being fixed up first.
 */
export const validateForm = (section, form, initial = {}) => {
  const errors = {};
  const editable = (field) => field.type !== 'boolean' && field.type !== 'timestamp';

  for (const field of section.fields) {
    if (editable(field) && isBlank(form[field.key])) errors[field.key] = 'Required';
  }

  // Bounded dates are checked whether or not the schedule is active, but only
  // where the user actually picked something — a device that came back holding
  // an expired window must stay saveable without first re-dating it.
  for (const field of section.fields) {
    if (field.type !== 'date' || errors[field.key]) continue;
    const value = form[field.key];
    if (!value || value === initial[field.key]) continue;

    const { min, max } = dateBounds(field);
    if (min && value < min) {
      errors[field.key] =
        field.minOffsetDays === 0 ? 'Cannot be in the past' : `Must be at least ${field.minOffsetDays} days from today`;
    } else if (max && value > max) {
      errors[field.key] = `Must be within ${field.maxOffsetDays} days from today`;
    }
  }

  // Ordering: a field declaring `after` cannot precede the one it names. Only
  // the date pair carries one — ON and OFF are deliberately unordered, since a
  // schedule may run past midnight. An inclusive rule lets the two be equal, so
  // a window that starts and ends on the same day is fine.
  for (const field of section.fields) {
    const rule = field.after;
    if (!rule || errors[field.key]) continue;

    const value = form[field.key];
    const other = form[rule.field];
    if (isBlank(value) || isBlank(other)) continue;

    if (rule.inclusive ? value < other : value <= other) errors[field.key] = rule.message;
  }

  return errors;
};
